import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import type { IInstallJob } from "../state/deviceInstallerSlice";
import {
  clearInstallJob,
  getConnectedDevice,
  getInstallJob,
  updateInstallStatus,
} from "../state/deviceInstallerSlice";
import { cancelCurrentInstall } from "../utils/adbInstaller";
import { launchApp } from "../utils/appLauncher";
import { launchViaAgent, cancelAgentInstall } from "../utils/agentInstaller";
import { useLaunchApp } from "../hooks/useLaunchApp";
import {
  VERIFICATION_FAILURE_SENTINEL,
  CANCELLED_SENTINEL,
  PROGRESS_SX,
  SPATIAL_STUDIO_PACKAGE_NAME,
} from "../constants";
import { deriveInstallJobStatus } from "../utils/jobStatus";
import { detectDeviceFamily } from "../utils/platformCompat";
import {
  CheckIcon,
  DownloadArrowIcon,
  PlayIcon,
  Spinner,
  WarningCircleIcon,
  CloseIcon,
} from "./icons";
import ModalShell from "./ModalShell";
import StepperProgress from "./StepperProgress";
import MediaPushModal from "./MediaPushModal";
import LinearProgress from "@mui/material/LinearProgress";

interface Props {
  onClose: () => void;
}

/** Show a "check your device" hint after this delay when installing on Android mobile */
const INSTALL_CONFIRM_HINT_MS = 5_000;

const STEPS = ["downloading", "installing"] as const;
type Step = typeof STEPS[number];

const STEP_LABEL_KEYS: Record<Step, string> = {
  downloading: "deviceInstaller.stepDownload",
  installing: "deviceInstaller.stepInstall",
};

/**
 * Map internal status to visual step index.
 * downloading = step 0
 * extracting/pushing/installing = step 1 (all shown as "Installation")
 * success = past all steps
 * error/cancelled = freeze at current step (handled separately)
 */
function getStepIndex(status: IInstallJob["status"]): number {
  if (status === "downloading") return 0;
  if (status === "extracting" || status === "pushing" || status === "installing") return 1;
  if (status === "success") return STEPS.length;
  return -1;
}

function isIndeterminate(status: IInstallJob["status"]): boolean {
  return status === "extracting" || status === "installing";
}

const InstallProgressModal = ({ onClose }: Props) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const installJob = useAppSelector(getInstallJob);
  const device = useAppSelector(getConnectedDevice);
  const [showMediaPush, setShowMediaPush] = useState(false);
  const [showInstallHint, setShowInstallHint] = useState(false);

  const isInstalling = installJob?.status === "installing";
  const isAndroidMobile =
    device?.productName != null && detectDeviceFamily(device.productName) === "android";

  useEffect(() => {
    if (!isInstalling || !isAndroidMobile) {
      setShowInstallHint(false);
      return undefined;
    }
    const timer = setTimeout(() => setShowInstallHint(true), INSTALL_CONFIRM_HINT_MS);
    return () => clearTimeout(timer);
  }, [isInstalling, isAndroidMobile]);

  const isAgentInstall = !!installJob?.agentAppId;

  const onLaunch = useCallback(async () => {
    if (!installJob?.packageName) throw new Error("No package name");
    if (isAgentInstall && installJob.agentAppId) {
      await launchViaAgent(installJob.agentAppId);
    } else {
      await launchApp(installJob.packageName);
    }
  }, [installJob?.packageName, installJob?.agentAppId, isAgentInstall]);
  const {
    status: launchStatus,
    launch: handleLaunch,
    error: launchError,
  } = useLaunchApp(onLaunch, { persistStatus: true });

  if (!installJob) return null;

  const jobStatus = deriveInstallJobStatus(installJob);
  const { isCancelled, isSuccess, isError, isDone } = jobStatus;
  const canCancel = installJob.status === "downloading";
  const canLaunch =
    isSuccess && !!installJob.packageName && (device?.status === "ready" || isAgentInstall);
  const isSpatialStudio = installJob.packageName === SPATIAL_STUDIO_PACKAGE_NAME;
  const canPreloadMedia =
    isSuccess &&
    !!installJob.packageName &&
    !!installJob.projectGroupId &&
    !isSpatialStudio &&
    !isAgentInstall;

  const handleMediaPush = () => {
    if (!installJob.packageName || !installJob.projectGroupId) return;
    setShowMediaPush(true);
  };

  const handleClose = () => {
    dispatch(clearInstallJob());
    onClose();
  };

  const handleCancel = () => {
    if (isAgentInstall && installJob.agentJobId) {
      cancelAgentInstall(installJob.agentJobId);
      dispatch(updateInstallStatus({ status: "error", errorMessage: CANCELLED_SENTINEL }));
    } else {
      cancelCurrentInstall();
    }
  };

  const rawStepIndex = getStepIndex(installJob.status);
  const currentStepIndex = rawStepIndex >= 0 ? rawStepIndex : 0;

  return (
    <>
      <ModalShell
        title={t("deviceInstaller.installOnDevice")}
        width="34rem"
        bodyHeight="280px"
        onClose={isDone ? handleClose : handleCancel}
      >
        <div className="flex items-center justify-between bg-gray-50 rounded-lg px-3 py-2 mb-4 shrink-0">
          <span className="text-sm text-gray-600 truncate">{installJob.fileName}</span>
          {installJob.totalSizeMB != null && installJob.totalSizeMB > 0 && (
            <span className="text-xs text-gray-400 ml-2 whitespace-nowrap">
              {t("deviceInstaller.fileSize", { size: installJob.totalSizeMB })}
            </span>
          )}
        </div>

        <StepperProgress
          steps={STEPS}
          stepLabelKeys={STEP_LABEL_KEYS}
          currentStepIndex={currentStepIndex}
          jobStatus={jobStatus}
        />

        <div className="flex-1 flex flex-col justify-center min-h-0">
          {!isDone && (
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-green-500">
                  {installJob.status === "downloading" && t("deviceInstaller.downloading")}
                  {(installJob.status === "extracting" ||
                    installJob.status === "pushing" ||
                    installJob.status === "installing") &&
                    t("deviceInstaller.installing")}
                </span>
                {(installJob.status === "downloading" || installJob.status === "pushing") && (
                  <span className="text-xs text-gray-500">{installJob.progress}%</span>
                )}
              </div>
              {isIndeterminate(installJob.status) ? (
                <LinearProgress sx={PROGRESS_SX} />
              ) : (
                <LinearProgress
                  variant="determinate"
                  value={installJob.progress}
                  sx={PROGRESS_SX}
                />
              )}
              {showInstallHint && (
                <div className="flex items-start gap-2 mt-3 p-2 bg-amber-50 rounded-lg">
                  <WarningCircleIcon className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  <span className="text-xs text-amber-700">
                    {t("deviceInstaller.installConfirmHint")}
                  </span>
                </div>
              )}
            </div>
          )}

          {isSuccess && (
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full flex items-center justify-center bg-green-50">
                {launchStatus === "launched" ? (
                  <PlayIcon className="w-8 h-8 text-green-500" />
                ) : (
                  <CheckIcon className="w-8 h-8 text-green-500" />
                )}
              </div>
              <span className="text-sm font-medium text-green-500">
                {launchStatus === "launched"
                  ? t("deviceInstaller.launchSuccess")
                  : t("deviceInstaller.installSuccess")}
              </span>
              {launchStatus === "error" && launchError && (
                <span className="text-xs text-red-500 text-center">
                  {t("deviceInstaller.launchFailed", { error: launchError })}
                </span>
              )}
            </div>
          )}

          {isCancelled && (
            <div className="flex items-center gap-2 bg-gray-50 rounded-lg p-3">
              <CloseIcon className="w-5 h-5 text-gray-400 shrink-0" />
              <span className="text-sm text-gray-500">{t("deviceInstaller.installCancelled")}</span>
            </div>
          )}

          {isError && (
            <div className="flex items-start gap-2 bg-red-50 rounded-lg p-3">
              <WarningCircleIcon className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
              <div className="text-sm text-red-600">
                {installJob.errorMessage === VERIFICATION_FAILURE_SENTINEL ? (
                  <>
                    <p className="font-medium">{t("deviceInstaller.verificationFailureTitle")}</p>
                    <p className="mt-1 text-red-500">
                      {t("deviceInstaller.verificationFailureHint")}
                    </p>
                  </>
                ) : (
                  <p>
                    {t("deviceInstaller.installFailed", {
                      error: installJob.errorMessage || "Unknown error",
                    })}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100 shrink-0">
          {canCancel && (
            <button
              onClick={handleCancel}
              className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              {t("deviceInstaller.cancel")}
            </button>
          )}
          {isDone && (
            <>
              {canLaunch && launchStatus !== "launched" && (
                <button
                  onClick={handleLaunch}
                  disabled={launchStatus === "launching"}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-60"
                >
                  {launchStatus === "launching" ? (
                    <Spinner className="w-3.5 h-3.5" color="green" />
                  ) : (
                    <PlayIcon className="w-3.5 h-3.5" />
                  )}
                  {launchStatus === "launching"
                    ? t("deviceInstaller.launching")
                    : launchStatus === "error"
                    ? t("deviceInstaller.retryLaunch")
                    : isAgentInstall
                    ? t("deviceInstaller.launchOnComputer")
                    : t("deviceInstaller.launchOnDevice")}
                </button>
              )}
              {canPreloadMedia && (
                <button
                  onClick={handleMediaPush}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
                >
                  <DownloadArrowIcon className="w-3.5 h-3.5" />
                  {t("deviceInstaller.mediaManageButton")}
                </button>
              )}
              <button
                onClick={handleClose}
                className={
                  canLaunch && launchStatus !== "launched"
                    ? "px-4 py-2 text-sm text-gray-500 hover:text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                    : "px-4 py-2 text-sm text-white bg-green-500 rounded-lg hover:opacity-90 transition-opacity"
                }
              >
                {t("general.close")}
              </button>
            </>
          )}
        </div>
      </ModalShell>
      {showMediaPush && installJob?.projectGroupId && installJob?.packageName && (
        <MediaPushModal
          projectGroupId={installJob.projectGroupId}
          packageName={installJob.packageName}
          mode={isAgentInstall ? "agent" : "adb"}
          appId={isAgentInstall ? installJob.agentAppId : undefined}
          onClose={() => setShowMediaPush(false)}
        />
      )}
    </>
  );
};

export default InstallProgressModal;
